import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:[stroke-width:1.3]',
  {
    variants: {
      variant: {
        default: 'bg-[#FF5983] text-white hover:bg-[#FF5983]/90',
        secondary: 'bg-[#393760] text-white hover:bg-[#393760]/90',
        outline:
          'bg-white border border-[rgba(86,83,134,0.17)] text-[#333333] hover:bg-[#F3F3FF]',
        destructive: 'bg-[#dc2626] text-[#fef2f2] hover:bg-[#dc2626]/90',
        ghost: 'hover:bg-[#F3F3FF] hover:text-[#333333]',
        link: 'text-[#5B57DB] underline-offset-2 hover:underline text-sm font-semibold',

        // Legacy variants for backwards compatibility
        fintalk: 'bg-fintalk text-fintalk-foreground hover:bg-fintalk/80',
        botEnvironment:
          'bg-sidebar-primary !text-sidebar-primary-foreground hover:bg-sidebar-primary-hover',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-6 rounded-xl px-2',
        sm: 'h-9 rounded-xl px-3',
        lg: 'h-11 rounded-xl px-8',
        icon: 'h-10 w-10',
        fit: 'w-fit h-fit',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
